/**
 * Store sessione admin.
 *
 * La sessione vera è il cookie firmato gestito dal server (@fastify/secure-session):
 * lo store tiene solo lo stato client (`authenticated`, `role`) per pilotare
 * router guard e UI. `checked` indica se la sessione è già stata verificata
 * almeno una volta contro `GET /api/admin/me`.
 *
 * Sprint 006: nessuna differenziazione admin/superadmin — `role` è tracciato
 * ma non ancora usato dalla UI.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '../lib/api.js'

export const useAuthStore = defineStore('auth', () => {
  const authenticated = ref(false)
  const role = ref(null)
  const checked = ref(false)

  /** Ripristina la sessione da cookie. Non lancia: in errore → non autenticato. */
  async function checkSession () {
    try {
      const data = await api.get('admin/me').json()
      authenticated.value = true
      role.value = data.role ?? null
    } catch {
      authenticated.value = false
      role.value = null
    } finally {
      checked.value = true
    }
  }

  /**
   * Login con PIN. Ritorna `{ ok }` e, in caso di errore, lo `status` HTTP
   * (401 PIN errato, 429 lockout) per il feedback UI.
   */
  async function login (pin) {
    try {
      await api.post('admin/login', { json: { pin } })
      await checkSession()
      return { ok: authenticated.value }
    } catch (err) {
      authenticated.value = false
      role.value = null
      return { ok: false, status: err?.response?.status ?? 0 }
    }
  }

  async function logout () {
    try {
      await api.post('admin/logout')
    } catch {
      // best-effort: lo stato locale viene comunque resettato
    }
    authenticated.value = false
    role.value = null
  }

  /**
   * Eleva il ruolo della sessione corrente senza fare logout. Tipico uso:
   * admin che inserisce il PIN di SUPERADMIN per accedere ad azioni
   * privilegiate. A differenza di `login()`, su PIN errato NON azzera la
   * sessione precedente (rinegozia con `checkSession`).
   *
   * Ritorna `{ ok, role, status }`.
   */
  async function escalate (pin) {
    try {
      await api.post('admin/login', { json: { pin } })
      await checkSession()
      return { ok: authenticated.value, role: role.value }
    } catch (err) {
      // Login fallito: il vecchio cookie di sessione resta valido lato server
      // perché un POST /login senza Set-Cookie non lo tocca. Rinfresca lo
      // stato locale per evitare drift.
      await checkSession()
      return { ok: false, status: err?.response?.status ?? 0 }
    }
  }

  /**
   * Rinuncia ai privilegi superadmin tornando 'admin' senza logout
   * (de-escalation). Inverso di `escalate`: non serve PIN. Su success aggiorna
   * lo stato locale via `checkSession`.
   *
   * Ritorna `{ ok, role, status }`.
   */
  async function stepDown () {
    try {
      await api.post('admin/session/step-down')
      await checkSession()
      return { ok: authenticated.value, role: role.value }
    } catch (err) {
      await checkSession()
      return { ok: false, status: err?.response?.status ?? 0 }
    }
  }

  return { authenticated, role, checked, checkSession, login, logout, escalate, stepDown }
})
